'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@achra/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { LucideExternalLink } from 'lucide-react';
import Link from 'next/link';
import { ConnectIsotype } from '../svgs';
const connectLinkVariants = cva('group transition-colors', {
  variants: {
    variant: {
      default: 'text-foreground hover:text-accent-foreground aria-disabled:hover:text-foreground',
      primary: 'text-primary-foreground'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
const connectLinkInnerVariants = cva('outline-border flex items-center gap-2 rounded-xl py-[6px] pr-4 pl-2 outline-2 transition-colors', {
  variants: {
    variant: {
      default: 'bg-card group-hover:bg-accent group-aria-disabled:group-hover:bg-card',
      primary: 'bg-primary group-hover:bg-primary/90 group-aria-disabled:group-hover:bg-primary'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
const connectIsotypeVariants = cva('size-10 shrink-0 p-1.5 transition-colors', {
  variants: {
    variant: {
      default: 'text-foreground group-hover:text-accent-foreground group-aria-disabled:group-hover:text-foreground',
      primary: 'text-primary-foreground'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
type ConnectLinkProps = Omit<React.ComponentProps<typeof Link>, 'children'> & VariantProps<typeof connectLinkVariants> & {
  action?: 'open' | 'edit';
  driveName: string;
  disabled?: boolean;
};
function ConnectLink(t0) {
  const $ = _c(43);
  let className;
  let disabled;
  let driveName;
  let href;
  let props;
  let t1;
  let variant;
  if ($[0] !== t0) {
    ({
      href,
      action: t1,
      driveName,
      disabled,
      variant,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = disabled;
    $[3] = driveName;
    $[4] = href;
    $[5] = props;
    $[6] = t1;
    $[7] = variant;
  } else {
    className = $[1];
    disabled = $[2];
    driveName = $[3];
    href = $[4];
    props = $[5];
    t1 = $[6];
    variant = $[7];
  }
  const action = t1 === undefined ? "open" : t1;
  let t2;
  if ($[8] !== disabled || $[9] !== props) {
    t2 = e => {
      if (disabled) {
        e.preventDefault();
      }
      if (props.onClick) {
        props.onClick(e);
      }
    };
    $[8] = disabled;
    $[9] = props;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const onClickOverride = t2;
  const t3 = disabled ? "void:0" : href;
  const t4 = disabled ? true : undefined;
  let t5;
  if ($[11] !== className || $[12] !== disabled || $[13] !== variant) {
    t5 = cn(connectLinkVariants({
      variant
    }), disabled && "cursor-not-allowed opacity-100", className);
    $[11] = className;
    $[12] = disabled;
    $[13] = variant;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== variant) {
    t6 = connectLinkInnerVariants({
      variant
    });
    $[15] = variant;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== variant) {
    t7 = connectIsotypeVariants({
      variant
    });
    $[17] = variant;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t7) {
    t8 = <ConnectIsotype aria-label="Connect logo" className={t7} />;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const t9 = action === "edit" ? "Edit" : "Open";
  let t10;
  if ($[21] !== t9) {
    t10 = <span className="text-[11px]/[18px] uppercase">{t9} in Connect</span>;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <LucideExternalLink className="size-3" />;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t10) {
    t12 = <div className="flex items-center gap-1">{t10}{t11}</div>;
    $[24] = t10;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== driveName) {
    t13 = <div className="text-4 truncate font-semibold">{driveName}</div>;
    $[26] = driveName;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t12 || $[29] !== t13) {
    t14 = <div className="flex min-w-0 flex-col">{t12}{t13}</div>;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t14 || $[32] !== t6 || $[33] !== t8) {
    t15 = <div className={t6}>{t8}{t14}</div>;
    $[31] = t14;
    $[32] = t6;
    $[33] = t8;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== action || $[36] !== onClickOverride || $[37] !== props || $[38] !== t15 || $[39] !== t3 || $[40] !== t4 || $[41] !== t5) {
    t16 = <Link href={t3} target="_blank" aria-disabled={t4} data-action={action} onClick={onClickOverride} className={t5} {...props}>{t15}</Link>;
    $[35] = action;
    $[36] = onClickOverride;
    $[37] = props;
    $[38] = t15;
    $[39] = t3;
    $[40] = t4;
    $[41] = t5;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  return t16;
}
export { ConnectLink };